import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Paper, IconButton, Box, Typography, CircularProgress } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import UrsHeader from '../../components/UrsHeader';
import HelpDeskPreviewDrawer from '../../components/HelpDeskPreviewDrawer';
import HelpDeskEditor from './HelpDeskEditor';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { menu, helpdesk, crm, telecomunication } from '../../translate';
import Api from '../../Api';
import './helpdesk.css';

const CATEGORY_KEYS = [
  { value: 'employees', menuKey: 'worker', source: 'menu' },
  { value: 'groups', menuKey: 'group', source: 'menu' },
  { value: 'ivrs', menuKey: 'ivr', source: 'menu' },
  { value: 'services', menuKey: 'shortF', source: 'menu' },
  { value: 'did', menuKey: 'did', source: 'menu' },
  { value: 'file', menuKey: 'file', source: 'menu' },
  { value: 'autoCall', menuKey: 'autoCall', source: 'menu' },
  { value: 'crm', menuKey: 'crm', source: 'crm' },
  { value: 'telegram', menuKey: 'telegram_title', source: 'telecomunication' },
  { value: 'whatsapp', menuKey: 'whatsapp_title', source: 'telecomunication' },
  { value: 'operatorReport', menuKey: 'operatorReport', source: 'menu' },
  { value: 'provide', menuKey: 'provide', source: 'menu' },
  { value: 'other', menuKey: 'helpdesk_other', source: 'helpdesk' },
];
const VALID_CATEGORY_VALUES = CATEGORY_KEYS.map((c) => c.value);

const SOURCE_MAP = { menu, helpdesk, crm, telecomunication };

function getCategoryOptions(lang) {
  const langIndex = Math.min(Math.max(1, +(lang || 1)), 3);
  return CATEGORY_KEYS.map(({ value, menuKey, source }) => {
    let label;
    const dict = SOURCE_MAP[source];
    if (dict && dict[menuKey]) label = dict[menuKey][langIndex];
    else label = value;
    return { value, label };
  });
}

function getCategoryLabel(value, lang) {
  const opts = getCategoryOptions(lang);
  const found = opts.find((o) => o.value === value);
  return found ? found.label : value;
}

const slugify = (text) =>
  String(text)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '');

const now = () => new Date().toISOString();

const LANG_INDEXES = [1, 2, 3];

function emptyTranslations() {
  return ['', '', '', ''];
}

function ensureTranslations(val) {
  if (Array.isArray(val) && val.length >= 4) return val;
  const s = typeof val === 'string' ? val : '';
  return ['', s, s, s];
}

function normalizeItem(item) {
  if (!item) return item;
  return {
    ...item,
    title: ensureTranslations(item.title),
    slug: ensureTranslations(item.slug),
    content: ensureTranslations(item.content),
    excerpt: ensureTranslations(item.excerpt),
  };
}

function getItemText(item, field, lang) {
  if (!item) return '';
  const val = item[field];
  if (Array.isArray(val)) {
    const idx = Math.min(3, Math.max(1, +(lang || 1)));
    return (val[idx] ?? val[1] ?? '').trim();
  }
  return typeof val === 'string' ? val : '';
}

function HelpDesk() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState('list');
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [previewItem, setPreviewItem] = useState(null);
  const [form, setForm] = useState({
    title: emptyTranslations(),
    slug: emptyTranslations(),
    category: 'other',
    status: 'draft',
    content: emptyTranslations(),
    excerpt: emptyTranslations(),
    updatedAt: '',
  });
  const [activeLang, setActiveLang] = useState(1);
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const lang = Math.min(3, Math.max(1, +(localStorage.getItem('atsLang') || 1)));
  const categoryOptions = getCategoryOptions(lang);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await Api.getHelpDeskList();
      const list = (data?.items || []).map(normalizeItem);
      setItems(list);
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to load articles');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = items.filter((it) => {
    const titleText = getItemText(it, 'title', lang);
    const excerptText = getItemText(it, 'excerpt', lang);
    const catLabel = getCategoryLabel(it.category, lang);
    const matchSearch = !search.trim() || titleText.toLowerCase().includes(search.toLowerCase()) || (excerptText && excerptText.toLowerCase().includes(search.toLowerCase())) || (catLabel && catLabel.toLowerCase().includes(search.toLowerCase())) || it.category.toLowerCase().includes(search.toLowerCase());
    const matchCat = !categoryFilter || it.category === categoryFilter;
    const matchStatus = statusFilter === 'all' || it.status === statusFilter;
    return matchSearch && matchCat && matchStatus;
  });

  const handleNew = () => {
    setForm({
      title: emptyTranslations(),
      slug: emptyTranslations(),
      category: 'other',
      status: 'draft',
      content: emptyTranslations(),
      excerpt: emptyTranslations(),
      updatedAt: now(),
    });
    setActiveLang(lang);
    setFormError('');
    setEditingId(null);
    setView('edit');
  };

  const handleEdit = (item) => {
    const normalized = normalizeItem(item);
    const category = VALID_CATEGORY_VALUES.includes(item.category) ? item.category : 'other';
    setForm({
      title: normalized.title,
      slug: normalized.slug,
      category,
      status: item.status,
      content: normalized.content,
      excerpt: normalized.excerpt,
      updatedAt: item.updatedAt || now(),
    });
    setActiveLang(lang);
    setFormError('');
    setEditingId(item.id);
    setView('edit');
  };

  const handleTitleChange = (value) => {
    setForm((f) => {
      const nextTitle = [...(f.title || emptyTranslations())];
      nextTitle[activeLang] = value;
      const nextSlug = [...(f.slug || emptyTranslations())];
      nextSlug[activeLang] = slugify(value) || nextSlug[activeLang];
      return { ...f, title: nextTitle, slug: nextSlug };
    });
    setFormError('');
  };

  const handleSave = async () => {
    const titles = form.title || emptyTranslations();
    const hasTitle = LANG_INDEXES.some((i) => (titles[i] || '').trim());
    if (!hasTitle) {
      setFormError(helpdesk.article_title_required[lang] || 'Title is required.');
      return;
    }
    const slugs = form.slug || emptyTranslations();
    const payload = {
      title: LANG_INDEXES.reduce((arr, i) => {
        arr[i] = (titles[i] || '').trim();
        return arr;
      }, ['', '', '', '']),
      slug: LANG_INDEXES.reduce((arr, i) => {
        arr[i] = (slugs[i] || '').trim() || slugify(titles[i] || '');
        return arr;
      }, ['', '', '', '']),
      content: form.content || emptyTranslations(),
      excerpt: form.excerpt || emptyTranslations(),
      category: form.category,
      status: form.status,
    };
    try {
      if (editingId) {
        await Api.updateHelpDesk(editingId, payload);
        toast.success(helpdesk.article_updated[lang] || 'Article updated.');
      } else {
        await Api.createHelpDesk(payload);
        toast.success(helpdesk.article_created[lang] || 'Article created.');
      }
      setView('list');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to save');
    }
  };

  const handleDelete = async (item) => {
    const displayTitle = getItemText(item, 'title', lang);
    const msg = helpdesk.delete_confirm[lang] || 'Delete this article?';
    if (!window.confirm(`${msg} "${displayTitle}"`)) return;
    try {
      await Api.deleteHelpDesk(item.id);
      toast.success(helpdesk.article_deleted[lang] || 'Article deleted.');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to delete');
    }
  };

  return (
    <>
      <Helmet>
        <title>Help Desk Articles – Admin</title>
      </Helmet>
      <UrsHeader />
      <Paper
        elevation={0}
        sx={{
          m: 2,
          mx: 'auto',
          maxWidth: 1280,
          minHeight: 'calc(100vh - 100px)',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        <div className="hd-container" style={{ paddingTop: '1.5rem', paddingBottom: '2rem' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <IconButton
            size="small"
            onClick={() => navigate(-1)}
            sx={{ color: '#7ebc0a' }}
            aria-label="Go back"
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, m: 0 }}>
            {view === 'list' ? (helpdesk.help_desk_articles[lang] || 'Help Desk Articles') : (editingId ? (helpdesk.edit_article[lang] || 'Edit Article') : (helpdesk.new_article[lang] || 'New Article'))}
          </Typography>
        </Box>
        {view === 'list' && (
          <div className="hd-fade-enter hd-fade-enter-active">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 200, maxWidth: 320 }}>
                <SearchIcon sx={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 18 }} />
                <input
                  type="text"
                  className="hd-input"
                  placeholder={helpdesk.search_placeholder[lang] || 'Search by title or category...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ paddingLeft: 40 }}
                />
              </div>
              <select
                className="hd-input"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{ width: 'auto', minWidth: 140 }}
              >
                <option value="">{helpdesk.all_categories[lang] || 'All categories'}</option>
                {categoryOptions.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <select
                className="hd-input"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ width: 'auto', minWidth: 120 }}
              >
                <option value="all">All</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
              <button type="button" className="hd-btn hd-btn-primary" onClick={handleNew}>
                <AddIcon sx={{ fontSize: 18 }} />
                {helpdesk.new_article[lang] || 'New Article'}
              </button>
            </div>
            <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, py: 4 }}>
                  <CircularProgress sx={{ color: '#7ebc0a' }} />
                </Box>
              ) : filtered.length === 0 ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
                  <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>{helpdesk.no_articles_found[lang] || 'No articles found.'}</p>
                  <p style={{ fontSize: '0.875rem' }}>{helpdesk.try_filters[lang] || 'Try adjusting filters or add a new article.'}</p>
                </div>
              ) : (
                <table className="hd-table">
                  <thead>
                    <tr>
                      <th>{helpdesk.title[lang] || 'Title'}</th>
                      <th>{helpdesk.category[lang] || 'Category'}</th>
                      <th>Status</th>
                      <th>{helpdesk.last_updated[lang] || 'Last Updated'}</th>
                      <th style={{ width: 140 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((it) => (
                      <tr key={it.id}>
                        <td style={{ fontWeight: 500 }}>{getItemText(it, 'title', lang)}</td>
                        <td>{getCategoryLabel(it.category, lang)}</td>
                        <td>
                          <span className={`hd-badge hd-badge-${it.status}`}>{it.status}</span>
                        </td>
                        <td style={{ color: '#6b7280' }}>{new Date(it.updatedAt).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button type="button" className="hd-btn hd-btn-ghost" onClick={() => handleEdit(it)} title="Edit">
                              <EditIcon sx={{ fontSize: 16 }} />
                            </button>
                            <button type="button" className="hd-btn hd-btn-ghost" onClick={() => setPreviewItem(it)} title="Preview">
                              <VisibilityIcon sx={{ fontSize: 16 }} />
                            </button>
                            <button type="button" className="hd-btn hd-btn-ghost" onClick={() => handleDelete(it)} title="Delete" style={{ color: '#dc2626' }}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
              {(helpdesk.showing_x_of_y[lang] || 'Showing {n} of {total} articles').replace('{n}', filtered.length).replace('{total}', items.length)}
            </p>
          </div>
        )}

        {view === 'edit' && (
          <div className="hd-fade-enter hd-fade-enter-active">
            <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
              {LANG_INDEXES.map((langIdx) => (
                <button
                  key={langIdx}
                  type="button"
                  onClick={() => setActiveLang(langIdx)}
                  className="hd-btn hd-btn-secondary"
                  style={{
                    padding: '0.35rem 0.75rem',
                    fontSize: '0.8125rem',
                    fontWeight: activeLang === langIdx ? 600 : 400,
                    background: activeLang === langIdx ? 'rgba(126, 188, 10, 0.15)' : undefined,
                    color: activeLang === langIdx ? '#5a7a08' : undefined,
                  }}
                >
                  {langIdx === 1 ? (helpdesk.lang_en[lang] || 'EN') : langIdx === 2 ? (helpdesk.lang_ru[lang] || 'RU') : (helpdesk.lang_hy[lang] || 'HY')}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              <div style={{ flex: '2 1 480px', minWidth: 0 }}>
                <div style={{ marginBottom: '1rem' }}>
                  <label className="hd-label">{helpdesk.title[lang] || 'Title'} *</label>
                  <input
                    type="text"
                    className={`hd-input ${formError ? 'error' : ''}`}
                    value={(form.title || emptyTranslations())[activeLang] || ''}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder={helpdesk.article_title_placeholder[lang] || 'Article title'}
                  />
                  {formError && <p style={{ marginTop: 4, fontSize: '0.875rem', color: '#dc2626' }}>{formError}</p>}
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label className="hd-label">{helpdesk.slug[lang] || 'Slug'}</label>
                  <input
                    type="text"
                    className="hd-input"
                    value={(form.slug || emptyTranslations())[activeLang] || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm((f) => {
                        const next = [...(f.slug || emptyTranslations())];
                        next[activeLang] = val;
                        return { ...f, slug: next };
                      });
                    }}
                    placeholder="url-slug"
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label className="hd-label">{helpdesk.excerpt[lang] || 'Excerpt (max 160 characters)'}</label>
                  <textarea
                    className="hd-input"
                    value={(form.excerpt || emptyTranslations())[activeLang] || ''}
                    onChange={(e) => {
                      const val = e.target.value.slice(0, 160);
                      setForm((f) => {
                        const next = [...(f.excerpt || emptyTranslations())];
                        next[activeLang] = val;
                        return { ...f, excerpt: next };
                      });
                    }}
                    placeholder={helpdesk.short_summary_placeholder[lang] || 'Short summary'}
                    rows={2}
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 4 }}>{((form.excerpt || emptyTranslations())[activeLang] || '').length}/160</p>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label className="hd-label">{helpdesk.content[lang] || 'Content'}</label>
                  <HelpDeskEditor
                    value={(form.content || emptyTranslations())[activeLang] || ''}
                    onChange={(html) => {
                      setForm((f) => {
                        const next = [...(f.content || emptyTranslations())];
                        next[activeLang] = html;
                        return { ...f, content: next };
                      });
                    }}
                    placeholder={helpdesk.content_placeholder[lang] || 'Write your content...'}
                  />
                </div>
              </div>
              <div style={{ flex: '1 1 260px', minWidth: 260 }}>
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1.25rem' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label className="hd-label">{helpdesk.category[lang] || 'Category'}</label>
                    <select
                      className="hd-input"
                      value={form.category}
                      onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    >
                      {categoryOptions.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label className="hd-label">Status</label>
                    <select
                      className="hd-input"
                      value={form.status}
                      onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    <span className="hd-label">{helpdesk.last_updated[lang] || 'Last updated'}</span>
                    <p style={{ margin: 0 }}>{form.updatedAt ? new Date(form.updatedAt).toLocaleString() : '—'}</p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1.25rem' }}>
                    <button type="button" className="hd-btn hd-btn-primary" onClick={handleSave}>
                      {helpdesk.save[lang] || 'Save'}
                    </button>
                    <button type="button" className="hd-btn hd-btn-secondary" onClick={() => setView('list')}>
                      {helpdesk.cancel[lang] || 'Cancel'}
                    </button>
                    <button
                      type="button"
                      className="hd-btn hd-btn-ghost"
                      onClick={() => setPreviewItem({ ...form, id: editingId || 'new' })}
                    >
                      <VisibilityIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      {helpdesk.preview[lang] || 'Preview'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </Paper>
      {previewItem && (
        <HelpDeskPreviewDrawer
          open
          onClose={() => setPreviewItem(null)}
          items={items}
          item={normalizeItem(previewItem)}
          onSelectArticle={(article) => setPreviewItem(article)}
          lang={view === 'edit' ? activeLang : lang}
        />
      )}
    </>
  );
}

export default HelpDesk;
